import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

const lighting = [
  {
    id: 'basic',
    label: 'Grunnleggende Belysning',
    price: 'Inkludert',
    priceNote: 'Standard',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/a6062413e_14b978a72_ThermoAlder.png',
    description: 'Varmt LED-lys i tretone som skaper en rolig og avslappende atmosfære. Energieffektivt og jevnt lys over hele rommet.',
    pros: ['Varmt og avslappende lys', 'Energieffektiv LED', 'Jevn lysfordeling', 'Inkludert i standardpris'],
    mood: 'Rolig og tradisjonell',
    sound: 'Ingen',
  },
  {
    id: 'ambient',
    label: 'Ambient + Lyd',
    price: '+10 000 kr',
    priceNote: 'Tillegg',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/16d3dd2b9_RGB.png',
    description: 'RGB LED-system med justerbare farger (Ruben Design) kombinert med Bluetooth-lydanlegg. Skap den perfekte stemning med lys og musikk.',
    pros: ['RGB fargestyrbart lys', 'Bluetooth-lydanlegg inkludert', 'Justerbar stemning', 'Ruben Design system'],
    mood: 'Fleksibel og stemningsskapende',
    sound: 'Bluetooth-høyttaler',
  },
  {
    id: 'luxury',
    label: 'Luksus System',
    price: '+25 000 kr',
    priceNote: 'Premium',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/ab4701ae9_LaudioJBL.jpg',
    description: 'Premium pakke med RGB-lysterapi, JBL Bluetooth-høyttalere og trådløs styring. Toppklasse lyd og lys for en spa-aktig badstuopplevelse.',
    pros: ['Premium JBL Bluetooth-høyttaler', 'RGB lysterapi system', 'Trådløs styring', 'Spa-nivå opplevelse'],
    mood: 'Luksus spa-opplevelse',
    sound: 'JBL premium lyd',
  },
];

export default function InnvendigBelysning() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
        className="relative h-[500px] overflow-hidden">
        <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/16d3dd2b9_RGB.png"
          alt="Belysning" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-12 z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-sm tracking-widest font-body uppercase text-white/60 mb-4">Innvendig materialer · Artikkel 5</p>
            <h1 className="font-heading text-6xl md:text-7xl font-light leading-[1.0] text-white mb-4">Belysning og Lydanlegg</h1>
            <p className="font-body text-white/80 text-xl max-w-2xl leading-relaxed">
              Fra enkelt LED til premium JBL — skap den stemningen du ønsker
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
          <h2 className="font-heading text-4xl font-light text-[#2a1810] mb-6">Hvilke belysningsalternativer kan du velge?</h2>
          <p className="font-body text-lg text-foreground/70 leading-relaxed">
            Her er en kortfattet oversikt over belysnings- og lydvalgene du kan velge mellom. Fra enkelt LED til premium JBL-lyd og RGB-lysterapi.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {lighting.map((item, idx) => (
            <motion.div key={item.id}
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -6 }}
              className="bg-white border border-border/20 rounded-[12px] overflow-hidden hover:shadow-xl hover:border-[#2d5a3d] transition-all">
              <div className="relative h-56 overflow-hidden">
                <img src={item.img} alt={item.label} className="w-full h-full object-cover" />
                <div className="absolute top-4 right-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${item.priceNote === 'Standard' ? 'bg-[#2d5a3d] text-white' : item.priceNote === 'Premium' ? 'bg-[#2a1810] text-white' : 'bg-white/90 text-[#2a1810]'}`}>
                    {item.price}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-light text-[#2a1810] mb-3">{item.label}</h3>
                <p className="font-body text-foreground/70 leading-relaxed mb-4 text-base">{item.description}</p>
                <div className="grid grid-cols-1 gap-2 mb-4">
                  <div className="bg-secondary/30 rounded-[6px] p-3">
                    <p className="font-body text-xs text-foreground/50 uppercase tracking-widest">Stemning</p>
                    <p className="font-body text-base font-medium text-[#2a1810]">{item.mood}</p>
                  </div>
                  <div className="bg-secondary/30 rounded-[6px] p-3">
                    <p className="font-body text-xs text-foreground/50 uppercase tracking-widest">Lyd</p>
                    <p className="font-body text-base font-medium text-[#2a1810]">{item.sound}</p>
                  </div>
                </div>
                <div className="space-y-1">
                  {item.pros.map((pro, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check size={12} className="text-[#2d5a3d] flex-shrink-0" />
                      <span className="font-body text-base text-foreground/60">{pro}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-[#2a1810] to-[#3d2818] text-white rounded-[12px] p-10 mb-12">
          <h3 className="font-heading text-2xl font-light mb-4">Lysterapi i badstuen</h3>
          <p className="font-body text-white/80 leading-relaxed">
            Forskning viser at fargevalget i belysning påvirker opplevelsen direkte. Blåtoner fremmer avslapning, røde og oransje toner gir energi. Med RGB-systemet kan du skreddersy opplevelsen etter humøret — med bare én berøring.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <a href="/innvendig-vindu" className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-body text-sm tracking-widest hover:bg-foreground hover:text-background transition-all rounded-[4px]">
            <ArrowLeft size={16} /> Forrige: Vindu
          </a>
          <a href="/innvendig-ekstra" className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all">
            Neste: Ekstra <ArrowRight size={16} />
          </a>
        </div>
      </div>
      <Footer />
    </div>
  );
}